import type {
	CreateDashboardResponse,
	Dashboard,
	DashboardConfig,
	DashboardHistoryLog,
	DashboardHistoryResponse,
	DashboardListResponse,
	DashboardMeta,
	DashboardResponse,
	DashboardShareOptionsResponse,
	PanelDataRequest,
	PanelDataResponse,
	TimeseriesDownloadRequest,
	TimeseriesLiveStreamParams,
	TimeseriesTableRequest,
	TimeseriesTableResponse,
} from '../dashboardsApi'

import { demoApi } from './db'

export const dashboardApi = {
	getDashboards: async (__page?: number, __perPage?: number, fetchConfig = false): Promise<DashboardListResponse> => {
		// V1 returns Dashboard[]
		const res = await demoApi.get<Dashboard[]>(`/v1/dashboards?full-data=${fetchConfig}`)
		return {
			count: res.length,
			data: res.map(d => ({
				id: d.id,
				name: d.config.dashboardMeta.title,
				config: d.config,
			})),
		}
	},

	getFullDashboardData: async (dashboardId: number): Promise<DashboardResponse> => {
		const d = await demoApi.get<Dashboard>(`/v1/dashboards/${dashboardId}`)
		return {
			id: d.id,
			name: d.config.dashboardMeta.title,
			config: d.config,
		}
	},

	getDashboardShareOptions: async (): Promise<DashboardShareOptionsResponse> =>
		demoApi.get('/v1/dashboard-share-options'),

	createDashboard: async (dashboardMeta: DashboardMeta, config?: DashboardConfig): Promise<CreateDashboardResponse> =>
		demoApi.post('/v1/dashboards', { config: config ?? { dashboardMeta } }),

	deleteDashboard: async (dashboardId: number): Promise<void> => demoApi.delete(`/v1/dashboards/${dashboardId}`),

	updateDashboardConfig: async (dashboardId: number, config: DashboardConfig): Promise<Dashboard> =>
		demoApi.post(`/v1/dashboards/${dashboardId}`, { config }),

	shareDashboard: async (_dashboardId: number, _rolesPayload: unknown): Promise<void> => {
		// Demo dashboards carry no access control — sharing is a no-op.
	},

	fetchPanelData: async (requestBody: PanelDataRequest): Promise<PanelDataResponse> =>
		demoApi.post('/v1/panel-data', requestBody),

	getDashboardHistory: async (dashboardId: number): Promise<DashboardHistoryResponse> => {
		// V1 returns { logs: ..., count: ... }
		const res = await demoApi.get<{ logs: DashboardHistoryLog[]; count: number }>(`/v1/logs/dashboard/${dashboardId}`)
		return { data: res.logs }
	},

	renameDashboardHistoryLog: async (dashboardId: number, version: string, newName: string): Promise<void> => {
		// Loosely mapping to V1 rename
		// V1 expects: /v1/dashboards/{id} with event: 'version_edit' and version ID
		await demoApi.post(`/v1/dashboards/${dashboardId}`, { id: version, event: 'version_edit', name: newName })
	},

	rollbackDashboard: async (dashboardId: number, version: string): Promise<void> => {
		await demoApi.post(`/v1/dashboards/${dashboardId}`, { id: version, event: 'rollback' })
	},

	fetchAllLogTags: async (table: string): Promise<{ tags: string[] }> =>
		demoApi.get(`/v1/log-tags?table=${encodeURIComponent(table)}`),

	fetchTimeseriesTable: async (
		_request: TimeseriesTableRequest,
		_signal?: AbortSignal,
		_tenantId?: string,
	): Promise<TimeseriesTableResponse> => {
		// Timeseries table is a V3-only feature; return empty in demo
		return { data: [], count: 0 }
	},

	downloadTimeseriesTable: async (_request: TimeseriesDownloadRequest): Promise<Response> => {
		throw new Error('Timeseries download not supported in demo environment')
	},

	getTimeseriesLiveStreamUrl: (_params: TimeseriesLiveStreamParams): string => {
		return ''
	},
}
